import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Plus, Edit, Trash2, Mail, MessageSquare } from "lucide-react";
import { toast } from "react-hot-toast";
import {
  getAutoReplyTemplates,
  createAutoReplyTemplate,
  updateAutoReplyTemplate,
  deleteAutoReplyTemplate,
} from "../../api/cannedResponse";

export default function CannedResponsePage() {
  // State for templates and data
  const [templates, setTemplates] = useState([]);
  const [triggerTypes, setTriggerTypes] = useState([
    { value: "property", label: "Property Enquiry" },
    { value: "general", label: "General Enquiry" },
    { value: "appraisal", label: "Appraisal Request" },
    { value: "contact", label: "Contact Form" },
    { value: "callback", label: "Callback Request" },
  ]);
  const [stats, setStats] = useState({
    activeTemplates: 0,
    sentToday: 0,
    sentThisMonth: 0,
  });
  const [loading, setLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [operationInProgress, setOperationInProgress] = useState(false);

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  // Form state - updated to match API payload structure
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    body: '',
    trigger_type: '',
    status: true,
  });

  const getStatusColor = (status) => {
    if (status === true) {
      return "bg-green-100 text-green-700";
    } else {
      return "bg-yellow-100 text-yellow-700";
    }
  };

  const getTriggerLabel = (triggerValue) => {
    const trigger = triggerTypes.find((t) => t.value === triggerValue);
    return trigger ? trigger.label : triggerValue;
  };

  const handleEdit = (template) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name || '',
      subject: template.subject || '',
      body: template.body || '',
      trigger_type: template.trigger_type || '',
      status: template.status !== undefined ? template.status : true,
    });
    setShowEditDialog(true);
  };

  const handleDelete = (template) => {
    if (operationInProgress) {
      toast.error('Please wait for the current operation to complete');
      return;
    }

    setTemplateToDelete(template);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!templateToDelete) return;

    const templateId = templateToDelete.id;
    // Save original state for rollback
    const originalTemplates = [...templates];
    const deletedTemplate = templates.find(t => t.id === templateId);

    try {
      setOperationInProgress(true);

      // Optimistic update - remove from UI immediately
      setTemplates(templates.filter(t => t.id !== templateId));

      // Update stats optimistically
      if (deletedTemplate?.status) {
        setStats(prev => ({
          ...prev,
          activeTemplates: Math.max(0, prev.activeTemplates - 1)
        }));
      }

      // Delete from backend (with timeout)
      const deletePromise = deleteAutoReplyTemplate(templateId);
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout')), 10000)
      );

      await Promise.race([deletePromise, timeoutPromise]);

      clearCache();
      toast.success('Template deleted successfully');
      setShowDeleteModal(false);
      setTemplateToDelete(null);

      // Refresh in background to sync state
      setTimeout(() => {
        fetchTemplates(true).catch(console.error);
      }, 500);

    } catch (error) {
      // Rollback on error
      setTemplates(originalTemplates);
      if (deletedTemplate?.status) {
        setStats(prev => ({
          ...prev,
          activeTemplates: prev.activeTemplates + 1
        }));
      }

      const errorMessage = error.message === 'Request timeout'
        ? 'Delete request timed out. Please try again.'
        : 'Failed to delete template';
      toast.error(errorMessage);
      console.error('Delete error:', error);
      setShowDeleteModal(false);
      setTemplateToDelete(null);
    } finally {
      setOperationInProgress(false);
    }
  };

  // API functions with caching
  const fetchTemplates = async (skipCache = false) => {
    try {
      // Check sessionStorage cache first
      if (!skipCache) {
        const cachedData = sessionStorage.getItem('email_templates_cache');
        const cacheTimestamp = sessionStorage.getItem('email_templates_cache_time');

        if (cachedData && cacheTimestamp) {
          const cacheAge = Date.now() - parseInt(cacheTimestamp);
          // Use cache if less than 5 minutes old
          if (cacheAge < 300000) {
            const parsed = JSON.parse(cachedData);
            setTemplates(parsed.templates || []);
            setStats(parsed.stats || { activeTemplates: 0, sentToday: 0, sentThisMonth: 0 });
            setLoading(false);
            setInitialLoadComplete(true);
            return;
          }
        }
      }

      setLoading(true);
      const response = await getAutoReplyTemplates();

      // Handle the response structure with user_templates and analytics
      if (response.data) {
        const templateData = response.data.user_templates || [];
        const statsData = {
          activeTemplates: response.data.analytics?.active || 0,
          sentToday: response.data.analytics?.today || 0,
          sentThisMonth: response.data.analytics?.current_month || 0,
        };

        setTemplates(templateData);
        setStats(statsData);

        // Cache the data
        sessionStorage.setItem('email_templates_cache', JSON.stringify({
          templates: templateData,
          stats: statsData
        }));
        sessionStorage.setItem('email_templates_cache_time', Date.now().toString());
      }
    } catch (error) {
      toast.error('Failed to fetch templates');
      console.error('Fetch templates error:', error);
    } finally {
      setLoading(false);
      setInitialLoadComplete(true);
    }
  };

  // Clear cache helper
  const clearCache = () => {
    sessionStorage.removeItem('email_templates_cache');
    sessionStorage.removeItem('email_templates_cache_time');
    sessionStorage.removeItem('email_templates'); // Also clear the key used by templatesAPI
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      subject: '',
      body: '',
      trigger_type: '',
      status: true,
    });
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.subject || !formData.body || !formData.trigger_type) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (operationInProgress) {
      toast.error('Please wait for the current operation to complete');
      return;
    }

    try {
      setOperationInProgress(true);

      // Create with timeout
      const createPromise = createAutoReplyTemplate(formData);
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout')), 10000)
      );

      await Promise.race([createPromise, timeoutPromise]);

      setShowCreateDialog(false);
      resetForm();
      clearCache();
      toast.success('Template created successfully');

      // Refresh to get new template with ID
      fetchTemplates(true);
    } catch (error) {
      const errorMessage = error.message === 'Request timeout'
        ? 'Create request timed out. Please try again.'
        : 'Failed to create template';
      toast.error(errorMessage);
      console.error('Create error:', error);
    } finally {
      setOperationInProgress(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    if (!selectedTemplate) return;

    if (!formData.name || !formData.subject || !formData.body || !formData.trigger_type) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (operationInProgress) {
      toast.error('Please wait for the current operation to complete');
      return;
    }

    try {
      setOperationInProgress(true);

      // Optimistic update in UI
      const updatedTemplates = templates.map(t =>
        t.id === selectedTemplate.id
          ? { ...t, ...formData }
          : t
      );
      setTemplates(updatedTemplates);

      // Update with timeout
      const updatePromise = updateAutoReplyTemplate(selectedTemplate.id, formData);
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout')), 10000)
      );

      await Promise.race([updatePromise, timeoutPromise]);

      setShowEditDialog(false);
      setSelectedTemplate(null);
      resetForm();
      clearCache();
      toast.success('Template updated successfully');

      // Refresh in background
      setTimeout(() => {
        fetchTemplates(true).catch(console.error);
      }, 500);

    } catch (error) {
      const errorMessage = error.message === 'Request timeout'
        ? 'Update request timed out. Please try again.'
        : 'Failed to update template';
      toast.error(errorMessage);
      console.error('Update error:', error);
      // Refresh to get correct state
      fetchTemplates(true);
    } finally {
      setOperationInProgress(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchTemplates();
  }, []);

  // Skeleton Loader Component
  const SkeletonRow = () => (
    <tr className="animate-pulse">
      <td className="px-6 py-4">
        <div className="h-4 bg-slate-200 rounded w-3/4 mb-2"></div>
        <div className="h-3 bg-slate-200 rounded w-1/2"></div>
      </td>
      <td className="px-6 py-4"><div className="h-6 bg-slate-200 rounded w-24"></div></td>
      <td className="px-6 py-4"><div className="h-6 bg-slate-200 rounded w-16"></div></td>
      <td className="px-6 py-4"><div className="h-4 bg-slate-200 rounded w-12"></div></td>
      <td className="px-6 py-4"><div className="h-4 bg-slate-200 rounded w-20"></div></td>
      <td className="px-6 py-4"><div className="h-8 bg-slate-200 rounded w-16"></div></td>
    </tr>
  );

  return (
    <div className="min-h-screen bg-white">
      <main className="max-w-7xl mx-auto  py-2">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Automated Messages</h1>
          <p className="text-slate-600">Manage your automated email responses for property inquiries</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="rounded-xl">
            <CardContent className="p-8">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Active Templates</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">
                    {templates.filter(template => template.status === true).length}
                  </p>
                </div>
                <div className="bg-teal-100 p-3 rounded-full">
                  <MessageSquare className="w-6 h-6 text-teal-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-xl">
            <CardContent className="p-8">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Sent Today</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{stats.sentToday}</p>
                </div>
                <div className="bg-blue-100 p-3 rounded-full">
                  <Mail className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-xl">
            <CardContent className="p-8">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">This Month</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{stats.sentThisMonth}</p>
                </div>
                <div className="bg-green-100 p-3 rounded-full">
                  <Mail className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Templates Table */}
        <Card className="shadow-sm rounded-xl">
          <CardHeader className="bg-white border-b border-slate-200 rounded-t-xl">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
              <div className="flex-1 min-w-0">
                <CardTitle className="text-lg sm:text-xl text-slate-900">Email Templates</CardTitle>
                <p className="text-xs sm:text-sm text-slate-600 mt-1">Create and manage your automated response templates</p>
              </div>
              <Dialog open={showCreateDialog} onOpenChange={(open) => {
                setShowCreateDialog(open);
                if (open) {
                  resetForm();
                }
              }}>
                <DialogTrigger asChild>
                  <Button
                    className="bg-teal-600 hover:bg-teal-700 text-white shadow-sm w-full sm:w-auto"
                    disabled={operationInProgress}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    <span className="text-xs sm:text-sm">Create Template</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle className="text-xl text-slate-900">Create New Auto-Reply Template</DialogTitle>
                  </DialogHeader>

                  <form onSubmit={handleCreateSubmit} className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="template-name" className="text-sm font-medium text-slate-700">
                          Template Name
                        </Label>
                        <Input
                          id="template-name"
                          placeholder="e.g., Property Enquiry Thank You"
                          className="mt-1"
                          value={formData.name}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label htmlFor="trigger-type" className="text-sm font-medium text-slate-700">
                          Trigger Type
                        </Label>
                        <Select value={formData.trigger_type} onValueChange={(value) => handleInputChange('trigger_type', value)}>
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select trigger" />
                          </SelectTrigger>
                          <SelectContent>
                            {triggerTypes.map((trigger) => (
                              <SelectItem key={trigger.value} value={trigger.value}>
                                {trigger.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="email-subject" className="text-sm font-medium text-slate-700">
                        Email Subject
                      </Label>
                      <Input
                        id="email-subject"
                        placeholder="Thank you for your inquiry"
                        className="mt-1"
                        value={formData.subject}
                        onChange={(e) => handleInputChange('subject', e.target.value)}
                      />
                    </div>

                    <div>
                      <Label htmlFor="email-message" className="text-sm font-medium text-slate-700">
                        Email Body
                      </Label>
                      <Textarea
                        id="email-message"
                        className="min-h-48 mt-1"
                        placeholder="Hi {firstName},&#10;&#10;Thank you for your interest in {propertyAddress}...&#10;&#10;Available variables: {firstName}, {lastName}, {propertyAddress}, {agentName}, {agentPhone}, {companyName}, {companyPhone}"
                        value={formData.body}
                        onChange={(e) => handleInputChange('body', e.target.value)}
                      />
                    </div>

                    {/* Variables section commented out as requested */}
                    {/*
                    <div className="bg-slate-50 p-4 rounded-lg border">
                      <h4 className="font-medium text-slate-900 mb-3">Available Variables:</h4>
                      <div className="grid grid-cols-2 gap-2 text-sm text-slate-600">
                        <span>• {"{firstName}"} - Lead's first name</span>
                        <span>• {"{lastName}"} - Lead's last name</span>
                        <span>• {"{propertyAddress}"} - Property address</span>
                        <span>• {"{agentName}"} - Assigned agent name</span>
                        <span>• {"{agentPhone}"} - Agent phone number</span>
                        <span>• {"{companyName}"} - Company name</span>
                        <span>• {"{companyPhone}"} - Company phone</span>
                        <span>• {"{inquiryDate}"} - Date of inquiry</span>
                      </div>
                    </div>
                    */}

                    <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                      <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        className="bg-teal-600 hover:bg-teal-700"
                        disabled={operationInProgress}
                      >
                        {operationInProgress ? 'Creating...' : 'Create Template'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {/* Mobile Card View */}
            <div className="block md:hidden">
              {loading && !initialLoadComplete ? (
                <div className="space-y-3 p-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="bg-white border border-slate-200 rounded-lg p-4 animate-pulse">
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2 mb-3"></div>
                      <div className="flex gap-2">
                        <div className="h-6 bg-gray-200 rounded w-20"></div>
                        <div className="h-6 bg-gray-200 rounded w-16"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : templates.length === 0 ? (
                <div className="p-6 text-center text-slate-500">
                  <p className="text-sm">No templates found.</p>
                  <p className="text-xs mt-1">Create your first template to get started.</p>
                </div>
              ) : (
                <div className="space-y-3 p-4">
                  {templates.map((template) => (
                    <div key={template.id} className="bg-white border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition-colors">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-slate-900 text-sm truncate">{template.name}</h3>
                          <p className="text-xs text-slate-500 mt-1 truncate">{template.subject}</p>
                        </div>
                        <div className="flex items-center space-x-1 ml-2 flex-shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(template)}
                            className="hover:bg-slate-100 h-8 w-8 p-0"
                            disabled={operationInProgress}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(template)}
                            className="hover:bg-red-50 hover:text-red-600 h-8 w-8 p-0"
                            disabled={operationInProgress}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100">
                        <Badge variant="outline" className="text-xs font-medium">
                          {getTriggerLabel(template.trigger_type)}
                        </Badge>
                        <Badge className={`text-xs font-medium ${getStatusColor(template.status)}`}>
                          {template.status ? 'ACTIVE' : 'DRAFT'}
                        </Badge>
                        <span className="text-xs text-slate-600">
                          Used {template.usage || 0} times
                        </span>
                        {template.last_used && (
                          <span className="text-xs text-slate-500">
                            • Last: {template.last_used}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">Template Name</th>
                    <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">Trigger</th>
                    <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">Status</th>
                    <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">Usage</th>
                    <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">Last Used</th>
                    <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {loading && !initialLoadComplete ? (
                    // Show skeleton loaders during initial load
                    <>
                      <SkeletonRow />
                      <SkeletonRow />
                      <SkeletonRow />
                      <SkeletonRow />
                      <SkeletonRow />
                    </>
                  ) : templates.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center text-slate-500">
                        <p className="text-sm">No templates found.</p>
                        <p className="text-xs mt-1">Create your first template to get started.</p>
                      </td>
                    </tr>
                  ) : (
                    templates.map((template) => (
                      <tr key={template.id} className="hover:bg-slate-50">
                        <td className="px-4 lg:px-6 py-4">
                          <div className="max-w-xs">
                            <div className="font-medium text-slate-900 text-sm truncate" title={template.name}>{template.name}</div>
                            <div className="text-xs text-slate-500 mt-1 truncate" title={template.subject}>{template.subject}</div>
                          </div>
                        </td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                          <Badge variant="outline" className="text-xs font-medium">
                            {getTriggerLabel(template.trigger_type)}
                          </Badge>
                        </td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                          <Badge className={`text-xs font-medium ${getStatusColor(template.status)}`}>
                            {template.status ? 'ACTIVE' : 'DRAFT'}
                          </Badge>
                        </td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-slate-900 font-medium">{template.usage || 0}</span>
                          <span className="text-sm text-slate-500 ml-1">times</span>
                        </td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-slate-600">{template.last_used || 'Never'}</span>
                        </td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(template)}
                              className="hover:bg-slate-100"
                              disabled={operationInProgress}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(template)}
                              className="hover:bg-red-50 hover:text-red-600"
                              disabled={operationInProgress}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Edit Template Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-xl text-slate-900">Edit Template: {selectedTemplate?.name}</DialogTitle>
            </DialogHeader>

            {selectedTemplate && (
              <form id="edit-form" onSubmit={handleEditSubmit} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-template-name" className="text-sm font-medium text-slate-700">
                      Template Name
                    </Label>
                    <Input
                      id="edit-template-name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-trigger-type" className="text-sm font-medium text-slate-700">
                      Trigger Type
                    </Label>
                    <Select value={formData.trigger_type} onValueChange={(value) => handleInputChange('trigger_type', value)}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {triggerTypes.map((trigger) => (
                          <SelectItem key={trigger.value} value={trigger.value}>
                            {trigger.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="edit-email-subject" className="text-sm font-medium text-slate-700">
                    Email Subject
                  </Label>
                  <Input
                    id="edit-email-subject"
                    value={formData.subject}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="edit-email-message" className="text-sm font-medium text-slate-700">
                    Email Body
                  </Label>
                  <Textarea
                    id="edit-email-message"
                    className="min-h-48 mt-1"
                    value={formData.body}
                    onChange={(e) => handleInputChange('body', e.target.value)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="template-status" className="text-sm font-medium text-slate-700">
                    Template Status
                  </Label>
                  <Select value={formData.status.toString()} onValueChange={(value) => handleInputChange('status', value === 'true')}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">Active</SelectItem>
                      <SelectItem value="false">Draft</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </form>
            )}

            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                form="edit-form"
                className="bg-teal-600 hover:bg-teal-700"
                disabled={operationInProgress}
              >
                {operationInProgress ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Modal */}
        {showDeleteModal && templateToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Delete Email Template
              </h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete the template <span className="font-medium">"{templateToDelete.name}"</span>? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setTemplateToDelete(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  disabled={operationInProgress}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                  disabled={operationInProgress}
                >
                  {operationInProgress ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}